import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Menu from './Menu';
import '../CSS/PaiePage.css';
import CountUp from 'react-countup';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import apiUrl from '../config';

const Profil = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [userDetails, setUserDetails] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(true);

  // États pour les compteurs
  const [documentsCount, setDocumentsCount] = useState(0);
  const [declarationsCount, setDeclarationsCount] = useState(0);
  const [fichesCount, setFichesCount] = useState(0);

  // Charger les détails utilisateur et les statistiques
  useEffect(() => {
    const fetchUserCounts = async () => {
      try {
        if (!userDetails) {
          const savedUser = JSON.parse(localStorage.getItem('selectedUser'));
          if (savedUser) {
            setUserDetails(savedUser);
          } else {
            navigate('/utilisateurs');
            return;
          }
        }

        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const userId = userDetails?.id;

        // Requêtes pour les compteurs
        const [docsResponse, declarationsResponse, fichesResponse] = await Promise.all([
          axios.get(`${apiUrl}/documents/${userId}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiUrl}/declarations/${userId}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiUrl}/fiche-paie/${userId}`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        setDocumentsCount(docsResponse.data.length);
        setDeclarationsCount(declarationsResponse.data.length);
        setFichesCount(fichesResponse.data.length);

        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données :', error);
        setLoading(false);
      }
    };

    fetchUserCounts();
  }, [userDetails, navigate]);

  if (loading) {
    return (
      <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '400px',  // Hauteur de l'espace du tableau
                        textAlign: 'center',
                        backgroundColor: '#f4f4f4', // Optionnel
                      }}
                    >
                      
                      <Typography variant="h6">Merci de patienter...</Typography>
                    </Box>
    );
  }

  if (!userDetails) {
    return <Typography variant="h6">Aucune donnée disponible.</Typography>;
  }

  return (
    <div className="paie-container">
      <Menu />
      <div className="content">
        {/* Titre principal */}
        <div className="header">
          <Typography variant="h1" align="center" sx={{ color: '#1f6391' }}>
            Détails du Profil
          </Typography>
        </div>

        {/* Sous-titre avec le statut du compte */}
        <Typography
          variant="h4"
          align="center"
          sx={{
            color: '#0B349D',
            fontWeight: 'bold',
            marginBottom: '30px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          Profil de {userDetails.nom} {userDetails.prenom}
          {userDetails.compte_status === 'Compte validé' ? (
            <CheckCircleIcon style={{ color: 'green' }} />
          ) : (
            <CancelIcon style={{ color: 'red' }} />
          )}
        </Typography>

        {/* Section des compteurs */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            marginBottom: '40px',
            padding: '20px',
            backgroundColor: '#f0f0f0',
            borderRadius: '10px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: '#1f6391', fontWeight: 'bold' }}>
              Documents
            </Typography>
            <Typography variant="h4" sx={{ color: '#0B349D', fontWeight: 'bold' }}>
              <CountUp start={0} end={documentsCount} duration={2} separator="," />
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: '#1f6391', fontWeight: 'bold' }}>
              Déclarations
            </Typography>
            <Typography variant="h4" sx={{ color: '#0B349D', fontWeight: 'bold' }}>
              <CountUp start={0} end={declarationsCount} duration={2} separator="," />
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: '#1f6391', fontWeight: 'bold' }}>
              Fiches de Paie
            </Typography>
            <Typography variant="h4" sx={{ color: '#0B349D', fontWeight: 'bold' }}>
              <CountUp start={0} end={fichesCount} duration={2} separator="," />
            </Typography>
          </Box>
        </Box>

        {/* Tableau des statistiques */}
        <Container maxWidth="lg" sx={{ p: 3 }}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: '10px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              backgroundColor: '#ffffff',
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ color: '#1f6391', fontWeight: 'bold' }}>Catégorie</TableCell>
                  <TableCell style={{ color: '#1f6391', fontWeight: 'bold' }}>Nombre</TableCell>
                  <TableCell style={{ color: '#1f6391', fontWeight: 'bold' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Documents</TableCell>
                  <TableCell>{documentsCount} Document(s)</TableCell>
                  <TableCell>
                    <button
                      className="button"
                      onClick={() => navigate('/Documents', { state: { userId: userDetails.id } })}
                    >
                      Voir les documents
                    </button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Déclarations</TableCell>
                  <TableCell>{declarationsCount} Déclaration(s)</TableCell>
                  <TableCell>
                    <button
                      className="button"
                      onClick={() => navigate('/Déclarations', { state: { userId: userDetails.id } })}
                    >
                      Voir les Déclarations
                    </button>
                  </TableCell>
                </TableRow>
                <TableRow>
  <TableCell>Fiches de Paie</TableCell>
  <TableCell>{fichesCount} Fiche(s) de Paie</TableCell>
  <TableCell>
    <button
      className="button"
      onClick={() => navigate('/Paie', { state: { userId: userDetails.id } })}
    >
      Voir les fiches de paie
    </button>
  </TableCell>
</TableRow>

              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </div>
    </div>
  );
};

export default Profil;
